import {
  addWalletAdmin,
  adminLogin,
  assignCardAdmin,
  buyCard,
  changePassword,
  confirmDeposit,
  confirmPassword,
  confirmWithdraw,
  createCardAdmin,
  createUserAdmin,
  deleteCard,
  deleteCardAdmin,
  deleteUserAdmin,
  deleteWallet,
  deposit,
  flagCardAdmin,
  freezeCard,
  getAuthSecret,
  getContactOtp,
  getOtp,
  login,
  register,
  rejectDeposit,
  rejectWithdraw,
  resetPassword,
  settleClient,
  submitTransactionHash,
  toggleUserStatus,
  transfer2Card,
  transfer2Wallet,
  transferCommision,
  unflagCardAdmin,
  unfreezeCard,
  updatePreferredCurrency,
  updateUser,
  updateUserAdmin,
  verifyCredentials,
  verifyEmail,
  withdraw,
} from "@/lib/api";
import { handleError } from "@/lib/helper";
import { useUser } from "@/zustand/store";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

export const useGetOTP = () =>
  useMutation({
    mutationFn: (data) => getOtp(data),
    onSuccess: () => {
      toast.success("An OTP has been sent to your email address");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useVerifyCredentials = () =>
  useMutation({
    mutationFn: (data) => verifyCredentials(data),
    onSuccess: () => {},
  });

export const useVerifyEmail = () =>
  useMutation({
    mutationFn: (data) => verifyEmail(data),
    onSuccess: () => {},
    onError: (error) => {
      handleError(error);
    },
  });

export const useGetContactOTP = () =>
  useMutation({
    mutationFn: (data) => getContactOtp(data),
    onSuccess: () => {
      toast.success("An OTP has been sent to your contact");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useGetAuthSecret = () =>
  useMutation({
    mutationFn: (data) => getAuthSecret(data),
    onSuccess: () => {
      toast.success("Please follow the instructions above");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useRegister = () =>
  useMutation({
    mutationFn: (data) => register(data),
    onError: (error) => {
      handleError(error);
    },
  });

export const useResetPassword = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => resetPassword(data),
    onSuccess: () => {
      toast.success("Password reset successful");
      onSuccess?.();
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useLogin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => login(data),
    onSuccess: (data) => {
      onSuccess(data);
      useUser.setState({ user: data.userData });
      sessionStorage.setItem("token", data.accessToken);
      toast.success("Login successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useAdminLogin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => adminLogin(data),
    onSuccess: (data) => {
      onSuccess(data);
      useUser.setState({ user: data.userData });
      sessionStorage.setItem("token", data.accessToken);
      toast.success("Login successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useUpdateUser = () =>
  useMutation({
    mutationFn: (data) => updateUser(data),
    onSuccess: () => {
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useUpdatePreferredCurrency = () =>
  useMutation({
    mutationFn: (currency) => updatePreferredCurrency(currency),
    onError: (error) => {
      handleError(error);
    },
  });
  
export const useDeposit = () =>
  useMutation({
    mutationFn: (data) => deposit(data),
    onError: (error) => {
      handleError(error);
    },
  });

export const useWithdraw = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => withdraw(data),
    onSuccess: () => {
      onSuccess();
      toast.success("Withdrawal request sent, please wait for approval");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useSubmitTransactionHash = ({ id }) =>
  useMutation({
    mutationFn: (data) => submitTransactionHash(id, data),
    onSuccess: () => {
      toast.success("Deposit pending, please wait");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useBuyCards = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => buyCard(data),
    onSuccess: () => {
      onSuccess();
      toast.success("Card purchase successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useFreezeCards = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => freezeCard(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card frozen successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useUnfreezeCards = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => unfreezeCard(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card unfreezed successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useDeleteCard = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => deleteCard(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card Deleted successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useTransfer2Card = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => transfer2Card(id, data),
    onSuccess: () => {
      onSuccess();
      toast.success("Transfer successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useTransfer2Wallet = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => transfer2Wallet(id, data),
    onSuccess: () => {
      onSuccess();
      toast.success("Transfer successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useChangePassword = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => changePassword(data),
    onSuccess: () => {
      onSuccess();
      toast.success("Password Changed Successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useConfirmPassword = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => confirmPassword(data),
    onSuccess: () => {
      onSuccess();
      toast.success("Account Confirmed");
    },
    onError: (error) => {
      handleError(error);
    },
  });

// Admin mutations

export const useAdminSettleClient = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => settleClient(id, data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Settlement Successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useFlagCardAdmin = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => flagCardAdmin(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card Freeze successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useAssignCardAdmin = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => assignCardAdmin(id, data),
    onSuccess: () => {
      onSuccess();
      toast.success("Card Assigned successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useUnflagCardAdmin = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => unflagCardAdmin(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card unfreeze successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useCreateCardAdmin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => createCardAdmin(data),
    onSuccess: () => {
      onSuccess();
      toast.success("Card created successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useDeleteCardAdmin = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: () => deleteCardAdmin(id),
    onSuccess: () => {
      onSuccess();
      toast.success("Card delete successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useCreateUserAdmin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => createUserAdmin(data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("User created successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useUpdateUserAdmin = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => updateUserAdmin(id, data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("User updated successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useToggleUserStatus = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => toggleUserStatus(data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("User status changed successful");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useDeleteUserAdmin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => deleteUserAdmin(data),
    onSuccess: () => {
      onSuccess();
      toast.success("User deleted successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useConfirmDeposit = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => confirmDeposit(data.depositId),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Deposit Confirmed");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useConfirmWithdraw = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => confirmWithdraw(data.withdrawId),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Withdrawal Confirmed");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useRejectDeposit = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => rejectDeposit(data.depositId),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Deposit Rejected");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useRejectWithdraw = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => rejectWithdraw(data.withdrawId),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Withdrawal Rejected");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useTransferCommision = ({ id, onSuccess }) =>
  useMutation({
    mutationFn: (data) => transferCommision(id, data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Commission transferred successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useAddWalletAdmin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => addWalletAdmin(data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Wallet added successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });

export const useDeleteWalletAdmin = ({ onSuccess }) =>
  useMutation({
    mutationFn: (data) => deleteWallet(data),
    onSuccess: () => {
      onSuccess?.();
      toast.success("Wallet deleted successfully");
    },
    onError: (error) => {
      handleError(error);
    },
  });
